import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Eye, EyeOff, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Reset link is missing the token');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      toast.success('Password reset! Sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed — request a new link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505] flex items-center justify-center px-4 pt-20"
      data-testid="reset-password-page"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0A0A0A] to-[#050505]" />
        <div className="absolute top-1/4 -right-1/4 w-1/2 h-1/2 bg-[#B5FF00]/5 blur-[100px] rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="font-['Josefin_Sans'] text-3xl font-bold text-[#B5FF00]">UNIGHT</span>
          </Link>
          <h1 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white mb-2">
            RESET PASSWORD
          </h1>
          <p className="text-zinc-400">Choose a new password for your account</p>
        </div>

        {!token ? (
          <div className="border border-red-500/30 bg-red-500/5 p-6 text-center" data-testid="reset-password-no-token">
            <AlertCircle size={36} className="text-red-400 mx-auto mb-4" />
            <p className="text-white mb-4">This reset link is invalid.</p>
            <Link to="/forgot-password" className="btn-primary inline-flex">
              REQUEST A NEW LINK
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">NEW PASSWORD</label>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white pl-11 pr-12 focus:border-[#B5FF00] focus:outline-none transition-colors"
                  placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
                  required
                  minLength={6}
                  autoFocus
                  data-testid="reset-new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="font-mono text-xs text-zinc-500 mt-2">Minimum 6 characters.</p>
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">CONFIRM PASSWORD</label>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white pl-11 pr-12 focus:border-[#B5FF00] focus:outline-none transition-colors"
                  placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
                  required
                  minLength={6}
                  data-testid="reset-confirm-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="reset-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  RESETTING...
                </>
              ) : (
                'SET NEW PASSWORD'
              )}
            </button>
          </form>
        )}

        <p className="text-center mt-8 text-zinc-500">
          <Link to="/login" className="text-[#B5FF00] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
